import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { SlidersHorizontal, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang } from "@/lib/i18n";
import { useProducts } from "@/lib/useProducts";
import { CATEGORY_META, COLLECTION_META, COLOR_META, formatPrice } from "@/lib/meta";
import ProductCard from "@/components/ProductCard";
import Placeholder from "@/components/Placeholder";

const ALL_SIZES = ["XS", "S", "M", "L", "XL", "Unique"];
const MAX_P = 2500;

export default function Shop() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const { data: products, isLoading } = useProducts();
  const [open, setOpen] = useState(false);
  const [sort, setSort] = useState("new");
  const [sel, setSel] = useState({
    categories: params.get("category") ? [params.get("category")] : [],
    sizes: [],
    colors: [],
    collections: [],
    maxPrice: MAX_P,
  });

  const toggle = (key, value) =>
    setSel((s) => ({
      ...s,
      [key]: s[key].includes(value) ? s[key].filter((v) => v !== value) : [...s[key], value],
    }));

  const filtered = useMemo(() => {
    let list = (products || []).filter(
      (p) =>
        (sel.categories.length === 0 || sel.categories.includes(p.category)) &&
        (sel.sizes.length === 0 || p.sizes.some((s) => sel.sizes.includes(s))) &&
        (sel.colors.length === 0 || p.colors.some((c) => sel.colors.includes(c))) &&
        (sel.collections.length === 0 || sel.collections.includes(p.collection)) &&
        p.price <= sel.maxPrice
    );
    if (sort === "priceAsc") list = [...list].sort((a, b) => a.price - b.price);
    else if (sort === "priceDesc") list = [...list].sort((a, b) => b.price - a.price);
    else list = [...list].sort((a, b) => (b.tag === "new" ? 1 : 0) - (a.tag === "new" ? 1 : 0));
    return list;
  }, [products, sel, sort]);

  const clearAll = () => setSel({ categories: [], sizes: [], colors: [], collections: [], maxPrice: MAX_P });

  const FilterPanel = () => (
    <div className="space-y-9" data-testid="shop-filter-panel">
      <FilterGroup title={t.shop.category} testKey="category">
        {Object.keys(CATEGORY_META).map((c) => (
          <CheckRow
            key={c}
            id={`shop-filter-category-${c}`}
            label={CATEGORY_META[c][lang]}
            checked={sel.categories.includes(c)}
            onChange={() => toggle("categories", c)}
          />
        ))}
      </FilterGroup>

      <FilterGroup title={t.shop.size} testKey="size">
        <div className="flex flex-wrap gap-2">
          {ALL_SIZES.map((s) => (
            <button
              key={s}
              data-testid={`shop-filter-size-${s}`}
              onClick={() => toggle("sizes", s)}
              className={`px-3.5 py-1.5 rounded-full border text-[12px] transition-all duration-200 ${
                sel.sizes.includes(s) ? "bg-ink text-ivory border-ink" : "border-ink/25 text-ink/70 hover:border-rose-deep hover:text-rose-deep"
              }`}
            >
              {SIZE_LABEL(s, lang)}
            </button>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup title={t.shop.color} testKey="color">
        <div className="flex flex-wrap gap-3">
          {Object.keys(COLOR_META).map((c) => (
            <button
              key={c}
              data-testid={`shop-filter-color-${c}`}
              title={COLOR_META[c][lang]}
              onClick={() => toggle("colors", c)}
              className={`w-8 h-8 rounded-full border-2 transition-all duration-200 ${
                sel.colors.includes(c) ? "border-rose-deep scale-110" : "border-ink/15 hover:border-ink/40"
              }`}
              style={{ backgroundColor: COLOR_META[c].hex }}
              aria-label={COLOR_META[c][lang]}
            />
          ))}
        </div>
      </FilterGroup>

      <FilterGroup title={t.shop.price} testKey="price">
        <input
          type="range"
          min="250"
          max={MAX_P}
          step="50"
          value={sel.maxPrice}
          onChange={(e) => setSel((s) => ({ ...s, maxPrice: Number(e.target.value) }))}
          className="w-full"
          data-testid="shop-filter-price-range"
          aria-label={t.shop.maxPrice}
        />
        <p className="text-[12px] text-ink/60 mt-2">
          {t.shop.maxPrice}: <span className="text-ink font-medium">{formatPrice(sel.maxPrice)}</span>
        </p>
      </FilterGroup>

      <FilterGroup title={t.shop.collection} testKey="collection">
        {Object.keys(COLLECTION_META).map((c) => (
          <CheckRow
            key={c}
            id={`shop-filter-collection-${c}`}
            label={COLLECTION_META[c][lang]}
            checked={sel.collections.includes(c)}
            onChange={() => toggle("collections", c)}
          />
        ))}
      </FilterGroup>

      <button onClick={clearAll} data-testid="shop-clear-filters" className="text-[12px] uppercase tracking-[0.14em] text-rose-deep hover:text-ink transition-colors">
        {t.shop.clearAll}
      </button>
    </div>
  );

  return (
    <div className="pt-12 lg:pt-16 pb-24" data-testid="shop-page">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <div className="mb-12">
          <p className="eyebrow mb-3">MIORA</p>
          <h1 className="font-serif font-light text-5xl lg:text-6xl text-ink">{t.shop.title}</h1>
          <p className="mt-3 text-ink/55 text-sm">{t.shop.sub}</p>
        </div>

        <div className="grid lg:grid-cols-[230px_1fr] gap-12">
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <FilterPanel />
            </div>
          </aside>

          <div>
            <div className="flex items-center justify-between gap-4 mb-8 pb-4 border-b border-ink/10">
              <p className="text-[12px] uppercase tracking-[0.14em] text-ink/55" data-testid="shop-result-count">
                {isLoading ? t.common.loading : `${filtered.length} ${t.shop.count}`}
              </p>
              <div className="flex items-center gap-3">
                <button
                  className="lg:hidden inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.12em] border border-ink/25 rounded-full px-4 py-2"
                  onClick={() => setOpen(true)}
                  data-testid="shop-open-filters"
                >
                  <SlidersHorizontal className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {t.shop.openFilters}
                </button>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="w-[190px] rounded-full border-ink/25 text-[12px] h-10" data-testid="shop-sort-select">
                    <SelectValue placeholder={t.shop.sortBy} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new" data-testid="shop-sort-new">{t.shop.sortNew}</SelectItem>
                    <SelectItem value="priceAsc" data-testid="shop-sort-asc">{t.shop.sortPriceAsc}</SelectItem>
                    <SelectItem value="priceDesc" data-testid="shop-sort-desc">{t.shop.sortPriceDesc}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {isLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-12">
                {[...Array(8)].map((_, i) => <Placeholder key={i} variant={i} aspect="aspect-[3/4]" label="" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-24" data-testid="shop-empty">
                <p className="font-serif text-2xl text-ink/70 mb-6">{t.shop.empty}</p>
                <button onClick={clearAll} className="btn btn-outline" data-testid="shop-empty-reset">{t.shop.emptyCta}</button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-12">
                {filtered.map((p, i) => (
                  <ProductCard key={p.slug} product={p} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-[70] lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-ink/40" onClick={() => setOpen(false)} />
            <motion.div
              className="absolute inset-y-0 start-0 w-[86%] max-w-sm bg-ivory p-6 overflow-y-auto"
              initial={{ x: lang === "ar" ? "100%" : "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: lang === "ar" ? "100%" : "-100%" }}
              transition={{ type: "tween", duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center justify-between mb-8">
                <p className="eyebrow">{t.shop.filters}</p>
                <button onClick={() => setOpen(false)} data-testid="shop-close-filters" aria-label={t.common.close} className="p-2">
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
              <FilterPanel />
              <button onClick={() => setOpen(false)} className="btn btn-ink w-full mt-10" data-testid="shop-apply-filters">
                {t.shop.apply} ({filtered.length})
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const SIZE_LABEL = (s, lang) => (s === "Unique" ? { fr: "Taille unique", en: "One size", ar: "مقاس واحد" }[lang] : s);

function FilterGroup({ title, children, testKey }) {
  return (
    <div data-testid={`shop-filter-group-${testKey}`}>
      <p className="eyebrow mb-4">{title}</p>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function CheckRow({ label, checked, onChange, id }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer group select-none">
      <span
        className={`w-4 h-4 rounded-[4px] border flex items-center justify-center transition-all duration-200 ${
          checked ? "bg-rose-deep border-rose-deep" : "border-ink/30 group-hover:border-rose-deep"
        }`}
      >
        {checked && (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M1.5 5.5L4 8L8.5 2.5" stroke="#FAF6F1" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <input type="checkbox" className="sr-only" checked={checked} onChange={onChange} data-testid={`${id}-checkbox`} />
      <span className="text-sm text-ink/75 group-hover:text-ink transition-colors">{label}</span>
    </label>
  );
}
